import React, { useRef, useCallback, useEffect } from 'react';
import type { SDKButtonInterface, LoomVideo } from '@loomhq/record-sdk';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { LoomIcon } from '@atlaskit/logo';
import { useFlagService, toFlagId } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { connect } from '@atlassian/jira-issue-view-react-redux';
import { createWebLinkRequest } from '@atlassian/jira-issue-view-store/src/actions/web-links-actions';
import {
	fireUIAnalytics,
	fireTrackAnalytics,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { toHref } from '@atlassian/jira-shared-types';
import QuickAddItemView from '../../quick-add-item';
import { SUBJECT_IDS } from '../constants';
import { useLoomSDK } from '../hooks/use-loom-sdk-context';
import messages from '../messages';
import loomMessages from './messages';

export type Props = {
	shouldBeCompact: boolean;
	onInsertLink: (title: string, url: string) => void;
};

const COPY_LINK_FLAG_ID = toFlagId('COPY_LINK_FLAG_ID');
const COPIED_FLAG_ID = toFlagId('COPIED_FLAG_ID');

const LoomRecordingButtonView = ({ shouldBeCompact, onInsertLink }: Props) => {
	const buttonRef = useRef<HTMLElement>(null);
	const runningSDKButtonRef = useRef<SDKButtonInterface | null>(null);
	const { showFlag } = useFlagService();
	const { formatMessage } = useIntl();
	const loomSdk = useLoomSDK();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onShowCopyLinkFlag = useCallback(
		(videoUrl: string) => {
			showFlag({
				id: COPY_LINK_FLAG_ID,
				type: 'success',
				testId:
					'issue-view-foundation.quick-add.loom-record-spike.quick-add-item-view.copy-link-flag',
				title: loomMessages.flagTitleNoTranslate,
				description: loomMessages.flagDescriptionNoTranslate,
				isAutoDismiss: true,
				actions: [
					{
						content: loomMessages.flagCopyLinkNoTranslate,
						onClick: () => {
							fireUIAnalytics(
								createAnalyticsEvent({
									action: 'clicked',
									actionSubject: 'button',
								}),
								'copyVideoLink',
							);
							navigator.clipboard.writeText(videoUrl).then(() => {
								showFlag({
									id: COPIED_FLAG_ID,
									type: 'success',
									testId:
										'issue-view-foundation.quick-add.loom-record-spike.quick-add-item-view.copied-flag',
									title: loomMessages.flagCopiedNoTranslate,
									isAutoDismiss: true,
								});
							});
						},
					},
				],
			});
		},
		[showFlag, createAnalyticsEvent],
	);

	const handleClick = () => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			SUBJECT_IDS.loomRecordVideoButton,
		);
	};

	const handleRecordingStartCallback = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'loomRecordingStart',
		);
	}, [createAnalyticsEvent]);

	const handleRecordingStartedCallback = useCallback(() => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'loomRecording started');
	}, [createAnalyticsEvent]);

	const handleRecordingCancelCallback = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'loomRecordingCancel',
		);
	}, [createAnalyticsEvent]);

	const handleRecordingCompleteCallback = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'loomRecordingComplete',
		);
	}, [createAnalyticsEvent]);

	const handleRecordingCompletedCallback = useCallback(() => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'loomRecording completed');
	}, [createAnalyticsEvent]);

	const handleRecordingUploadedCallback = useCallback(() => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'loomRecording uploaded');
	}, [createAnalyticsEvent]);

	const handleRecordingInsertClickCallback = useCallback(
		async (video: LoomVideo) => {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				}),
				'insertVideoLink',
			);
			onInsertLink(video.title, video.sharedUrl);
			fireTrackAnalytics(createAnalyticsEvent({}), 'loomVideoLink inserted');
			onShowCopyLinkFlag(video.sharedUrl);
		},
		[createAnalyticsEvent, onInsertLink, onShowCopyLinkFlag],
	);

	const handleRecordingOpenOnLoomCallback = useCallback(
		async (video: LoomVideo) => {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				}),
				'openOnLoom',
			);
			window.open(video.sharedUrl, '_blank');
		},
		[createAnalyticsEvent],
	);

	useEffect(
		() => () => {
			if (runningSDKButtonRef.current && loomSdk.sdk) {
				// if we're actively recording, and the button gets unmounted, it means the user has navigated away from issue view and
				// we have to change the behaviour of the CTA to "Open on Loom" instead of "Insert link" since that is no longer possible
				fireTrackAnalytics(createAnalyticsEvent({}), 'loomSDKButton unmountedWhileRecording');
				runningSDKButtonRef.current.off('insert-click', handleRecordingInsertClickCallback);
				runningSDKButtonRef.current.on('insert-click', handleRecordingOpenOnLoomCallback);
				loomSdk.sdk.updateConfig({
					config: {
						insertButtonText: 'Open on Loom',
					},
				});
			}
		},
		[
			createAnalyticsEvent,
			handleRecordingInsertClickCallback,
			handleRecordingOpenOnLoomCallback,
			loomSdk.sdk,
		],
	);

	useEffect(() => {
		const button = buttonRef.current;

		if (!loomSdk.initializing && !loomSdk.supported) {
			fireOperationalAnalytics(createAnalyticsEvent({}), 'loomSDK unsupported', {
				error: loomSdk.unsupportedError,
			});
			return;
		}

		if (!button || !loomSdk.sdk || loomSdk.initializing) return;

		const sdkButton = loomSdk.sdk.configureButton({ element: button });

		sdkButton.on('start', handleRecordingStartCallback);
		sdkButton.on('recording-start', handleRecordingStartedCallback);
		sdkButton.on('cancel', handleRecordingCancelCallback);
		sdkButton.on('complete', handleRecordingCompleteCallback);
		sdkButton.on('recording-complete', handleRecordingCompletedCallback);
		sdkButton.on('upload-complete', handleRecordingUploadedCallback);
		sdkButton.on('insert-click', handleRecordingInsertClickCallback);
		sdkButton.on('lifecycle-update', (state) => {
			// assign the sdkButtonRef only when we're actively recording
			if (state === 'active-recording') {
				runningSDKButtonRef.current = sdkButton;
			} else {
				runningSDKButtonRef.current = null;
			}
		});

		return () => {
			sdkButton.off('start', handleRecordingStartCallback);
			sdkButton.off('recording-start', handleRecordingStartedCallback);
			sdkButton.off('cancel', handleRecordingCancelCallback);
			sdkButton.off('complete', handleRecordingCompleteCallback);
			sdkButton.off('recording-complete', handleRecordingCompletedCallback);
			sdkButton.off('upload-complete', handleRecordingUploadedCallback);
			sdkButton.off('insert-click', handleRecordingInsertClickCallback);
		};
	}, [
		buttonRef,
		handleRecordingStartCallback,
		handleRecordingStartedCallback,
		handleRecordingCancelCallback,
		handleRecordingCompleteCallback,
		handleRecordingUploadedCallback,
		handleRecordingInsertClickCallback,
		handleRecordingCompletedCallback,
		loomSdk,
		createAnalyticsEvent,
	]);

	return (
		loomSdk.supported &&
		!loomSdk.initializing && (
			<QuickAddItemView
				key="loom-recording-button"
				id="loom-recording-button"
				icon={<LoomIcon size="small" label="" />}
				onClick={handleClick}
				ref={buttonRef}
				label={formatMessage(messages.loomRecordVideoButtonLabel)}
				compactMode={shouldBeCompact}
				testId="issue-view-foundation.quick-add.loom-record-item-ad.loom-recording-button.quick-add-item-view"
			/>
		)
	);
};

export const LoomRecordingButton = connect(null, (dispatch) => ({
	onInsertLink: (title: string, url: string) =>
		dispatch(createWebLinkRequest(toHref(url), title, toHref(''))),
}))(LoomRecordingButtonView);
